import { ErrorHandler, Injectable } from '@angular/core';
import type * as SentryBrowser from '@sentry/browser';

// Set only once initWebMonitoring resolves a real DSN. Sentry's SDK is
// dynamically imported below instead of statically at the top of this file
// so it never ships to visitors on the (common) deployments that don't
// configure SENTRY_WEB_DSN — a static import would put the whole SDK in
// the main bundle regardless of whether it's ever used.
let sentry: typeof SentryBrowser | undefined;

/**
 * Dashboard error tracking, pointed at a self-hosted BugSink instance (it
 * speaks the Sentry ingest protocol — see docs/monitoring.md). The DSN comes
 * from GET /api/platform-config at runtime (this app has no build-time
 * environment files; same delivery as widgetUrl), so an unset SENTRY_WEB_DSN
 * simply leaves all of this dormant.
 */
export async function initWebMonitoring(): Promise<void> {
  try {
    const response = await fetch('/api/platform-config');
    if (!response.ok) return;
    const config = await response.json();
    const dsn = config?.monitoring?.dsn;
    if (typeof dsn !== 'string' || !dsn) return;

    sentry = await import('@sentry/browser');
    sentry.init({
      dsn,
      environment: typeof config.monitoring.environment === 'string' ? config.monitoring.environment : undefined,
      // Error tracking only — BugSink doesn't do performance tracing.
      tracesSampleRate: 0,
    });
  } catch {
    // Monitoring must never break the app it monitors.
  }
}

/**
 * Keeps Angular's default console reporting AND forwards to BugSink once
 * the SDK has loaded. Before that (config fetch in flight, or no DSN
 * configured — so the SDK was never imported), forwarding is just skipped;
 * an error that happens before Sentry is initialized was never going to
 * reach BugSink anyway, static or dynamic import alike.
 */
@Injectable()
export class MonitoringErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    super.handleError(error);
    sentry?.captureException(error);
  }
}
